import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import AccountMultipleOutlineIcon from 'mdi-react/AccountMultipleOutlineIcon';
import CalendarRemoveOutlineIcon from 'mdi-react/CalendarRemoveOutlineIcon';
import RestoreIcon from 'mdi-react/RestoreIcon';
import Api from '../../../../util/api';
import { partial } from '../../../../util/translation/translation';
import FormCard from '../../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../../shared/components/custom/SquareIconButton';
import CancelOccurrenceModal from './CancelOccurrenceModal';
import { formatDay, formatDayTime, getApiErrorMessage } from '../seriesUtils';

// The occurrence's state, most important first
const occurrenceStatus = (occurrence, p) => {
  const now = moment();
  if (occurrence.cancelledAt) {
    return { label: p('statusCancelled'), color: 'secondary' };
  }
  if (moment(occurrence.startDate).isSameOrBefore(now)) {
    return { label: p('statusFinished'), color: 'secondary' };
  }
  if (
    occurrence.maxAttendees &&
    occurrence.bookedSeats >= occurrence.maxAttendees
  ) {
    return { label: p('statusFull'), color: 'warning' };
  }
  if (
    occurrence.registrationStart &&
    moment(occurrence.registrationStart).isAfter(now)
  ) {
    return {
      label: p('statusOpens', {
        date: formatDay(occurrence.registrationStart),
      }),
      color: 'primary',
    };
  }
  if (
    occurrence.registrationEnd &&
    moment(occurrence.registrationEnd).isBefore(now)
  ) {
    return { label: p('statusClosed'), color: 'danger' };
  }
  return { label: p('statusOpen'), color: 'success' };
};

// The series' occurrences week by week, with cancel/restore and extra dates
const SeriesOccurrences = ({
  series,
  occurrences,
  showPast,
  onShowPastChange,
  keptIds,
  reload,
  setLoading,
}) => {
  const p = partial('EventSeries');
  const history = useHistory();
  const [cancelling, setCancelling] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addDateOpen, setAddDateOpen] = useState(false);
  const [newDate, setNewDate] = useState('');

  const cancelOccurrence = async ({ channels, message }) => {
    setBusy(true);
    setLoading(true);
    try {
      await Api.series.cancelOccurrence(cancelling.id, { channels, message });
      toast.success(
        p('occurrenceCancelled', { date: formatDayTime(cancelling.startDate) })
      );
      setCancelling(null);
      await reload();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
      setLoading(false);
    }
  };

  const restoreOccurrence = async (occurrence) => {
    setLoading(true);
    try {
      await Api.series.restoreOccurrence(occurrence.id);
      toast.success(
        p('occurrenceRestored', { date: formatDayTime(occurrence.startDate) })
      );
      await reload();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const addOccurrence = async () => {
    if (!newDate) return;
    setBusy(true);
    setLoading(true);
    try {
      await Api.series.addOccurrence(
        series.id,
        new Date(newDate).toISOString()
      );
      toast.success(p('occurrenceAdded', { date: formatDayTime(newDate) }));
      setAddDateOpen(false);
      setNewDate('');
      await reload();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
      setLoading(false);
    }
  };

  // Week by week (ISO weeks, Monday first)
  const weeks = occurrences.reduce((groups, occurrence) => {
    const weekStart = moment(occurrence.startDate).startOf('isoWeek');
    const key = weekStart.format('YYYY-MM-DD');
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(occurrence);
    } else {
      groups.push({ key, weekStart, items: [occurrence] });
    }
    return groups;
  }, []);

  const renderRow = (occurrence) => {
    const status = occurrenceStatus(occurrence, p);
    const start = moment(occurrence.startDate);
    const isPast = start.isSameOrBefore(moment());
    return (
      <tr
        key={occurrence.id}
        className={occurrence.cancelledAt ? 'series-row--cancelled' : ''}
      >
        <td className="table-stack__primary">
          <Link to={`/events/edit/${occurrence.id}`}>
            {formatDay(occurrence.startDate)}
          </Link>
          {occurrence.changedByHand && (
            <Badge color="info" className="ml-2" pill>
              {p('changedByHand')}
            </Badge>
          )}
          {keptIds.includes(occurrence.id) && (
            <Badge color="warning" className="ml-2" pill>
              {p('notInSchedule')}
            </Badge>
          )}
          {occurrence.cancelledAt && occurrence.cancelMessage && (
            <small className="text-muted d-block">
              {occurrence.cancelMessage}
            </small>
          )}
        </td>
        <td data-label={p('time')}>
          {start.format('HH:mm')}–{moment(occurrence.endDate).format('HH:mm')}
        </td>
        <td data-label={p('booked')}>
          {occurrence.bookedSeats}
          {occurrence.maxAttendees ? ` / ${occurrence.maxAttendees}` : ''}
          {occurrence.waitingCount > 0 && (
            <Badge className="ml-1" pill color="warning">
              {p('waitlistCount', { count: occurrence.waitingCount })}
            </Badge>
          )}
        </td>
        <td data-label={p('status')}>
          <Badge color={status.color}>{status.label}</Badge>
        </td>
        <td className="table-stack__actions">
          <div className="icon-actions">
            <SquareIconButton
              icon={<AccountMultipleOutlineIcon />}
              title={p('participantsOfOccurrence')}
              id={`occurrence-participants-${occurrence.id}`}
              onClick={() =>
                history.push(`/events/edit/${occurrence.id}?tab=3`)
              }
            />
            <SquareIconButton
              icon={<EditOutlineIcon />}
              title={p('openOccurrence')}
              id={`occurrence-edit-${occurrence.id}`}
              onClick={() => history.push(`/events/edit/${occurrence.id}`)}
            />
            {occurrence.cancelledAt ? (
              <SquareIconButton
                icon={<RestoreIcon />}
                title={p('restore')}
                tone="primary"
                id={`occurrence-restore-${occurrence.id}`}
                disabled={isPast}
                onClick={() => restoreOccurrence(occurrence)}
              />
            ) : (
              <SquareIconButton
                icon={<CalendarRemoveOutlineIcon />}
                title={p('cancelOccurrence')}
                tone="danger"
                id={`occurrence-cancel-${occurrence.id}`}
                disabled={isPast}
                onClick={() => setCancelling(occurrence)}
              />
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <CancelOccurrenceModal
        occurrence={cancelling}
        busy={busy}
        onClose={() => setCancelling(null)}
        onConfirm={cancelOccurrence}
      />
      <Modal isOpen={addDateOpen} toggle={() => setAddDateOpen(false)} centered>
        <ModalHeader toggle={() => setAddDateOpen(false)}>
          {p('addDate')}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted" style={{ fontSize: 13 }}>
            {p('addDateHint')}
          </p>
          <label className="form-header d-block mb-1" htmlFor="seriesNewDate">
            {p('start')}
          </label>
          <Input
            id="seriesNewDate"
            type="datetime-local"
            value={newDate}
            min={moment().format('YYYY-MM-DDTHH:mm')}
            onChange={(event) => setNewDate(event.target.value)}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            className="mb-0"
            onClick={() => setAddDateOpen(false)}
          >
            {p('cancel')}
          </Button>
          <Button
            color="primary"
            className="mb-0"
            disabled={!newDate || busy}
            onClick={addOccurrence}
          >
            {p('addDate')}
          </Button>
        </ModalFooter>
      </Modal>
      <FormCard
        title={
          <>
            {p('tabOccurrences')}
            {occurrences.length > 0 && (
              <span className="form-card__count">{occurrences.length}</span>
            )}
          </>
        }
        actions={
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <button
              type="button"
              className={`stats-range${showPast ? ' active' : ''}`}
              aria-pressed={showPast}
              onClick={() => onShowPastChange(!showPast)}
            >
              {p('showPast')}
            </button>
            <Button
              size="sm"
              color="primary"
              className="mb-0"
              onClick={() => setAddDateOpen(true)}
            >
              {p('addDate')}
            </Button>
          </div>
        }
        bodyClassName="form-card__body--flush"
      >
        <Table responsive className="table--roomy table--stack mb-0">
          <thead>
            <tr>
              <th>{p('date')}</th>
              <th>{p('time')}</th>
              <th>{p('booked')}</th>
              <th>{p('status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {weeks.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted text-center py-4">
                  {p('noOccurrences')}
                </td>
              </tr>
            )}
            {weeks.map((week) => (
              <React.Fragment key={week.key}>
                <tr className="series-week-row">
                  <td colSpan={5}>
                    {p('weekLabel', {
                      week: week.weekStart.isoWeek(),
                      from: week.weekStart.format('D MMM'),
                      to: week.weekStart.clone().add(6, 'days').format('D MMM'),
                    })}
                  </td>
                </tr>
                {week.items.map(renderRow)}
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </FormCard>
    </>
  );
};

SeriesOccurrences.propTypes = {
  series: PropTypes.shape({ id: PropTypes.string }).isRequired,
  occurrences: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showPast: PropTypes.bool.isRequired,
  onShowPastChange: PropTypes.func.isRequired,
  keptIds: PropTypes.arrayOf(PropTypes.string),
  reload: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
};

SeriesOccurrences.defaultProps = {
  keptIds: [],
};

export default SeriesOccurrences;
